import { FirebaseError } from 'firebase/app';
import { FormikErrors, FormikTouched } from 'formik';
import { NavigateFunction } from 'react-router-dom';

import { ContextState } from '../../context/AuthContext';
import { authApi } from '../../services/api';
import { AuthApiError } from '../../services/auth/auth-api-error';
import { handleAuthApiError, handleFirebaseError, isMindy, isOms } from '../../utils';

export type SignUpFormValues = {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
};

type SubmitSignUpParams = {
    values: SignUpFormValues;
    inviteUuid: string;
    userExternalId?: string;
    context?: ContextState;
    navigate: NavigateFunction;
    setSignupSuccess: (value: boolean) => void;
    setSubmitting: (value: boolean) => void;
};

const LOGIN_RETRY_ATTEMPTS = 3;
const LOGIN_RETRY_DELAY_MS = 2000;
const LOGIN_FALLBACK_DELAY_MS = 1500;

export const fieldHasError = <T>(errors: FormikErrors<T>, touched: FormikTouched<T>, key: keyof T): boolean =>
    !!errors[key] && !!touched[key];

// Optional fields are only sent when the user filled them in
export const buildSignupPayload = (values: SignUpFormValues, userExternalId?: string) => {
    const { firstName, lastName, phone, ...rest } = values;
    return {
        ...rest,
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
        ...(phone ? { phone } : {}),
        externalId: userExternalId,
    };
};

export const appendToken = (url: string, token: string | null): string =>
    url.includes('?') ? `${url}&token=${token}` : `${url}?token=${token}`;

const buildPlatformRedirectUrl = async (): Promise<string> => {
    const redirectResponse = await authApi.redirect();
    return appendToken(redirectResponse.url, localStorage.getItem('token'));
};

// Retries login because the backend may need time to process the brand new user
const retryLogin = async (login: () => Promise<unknown>) => {
    let loginSuccess = false;
    for (let attempt = 0; attempt < LOGIN_RETRY_ATTEMPTS && !loginSuccess; attempt++) {
        try {
            await new Promise((resolve) => setTimeout(resolve, LOGIN_RETRY_DELAY_MS));
            await login();
            loginSuccess = true;
        } catch {
            console.log(`[SignUp] Auto-login attempt ${attempt + 1} failed, retrying...`);
            if (attempt === LOGIN_RETRY_ATTEMPTS - 1) throw new Error('Login failed after retries');
        }
    }
};

export const handleSignupError = (error: unknown, navigate: NavigateFunction) => {
    if (error instanceof FirebaseError) {
        const errorPath = handleFirebaseError(error);
        if (errorPath) navigate(...errorPath);
        return;
    }
    if (error instanceof AuthApiError) {
        const errorPath = handleAuthApiError(error);
        if (errorPath) navigate(...errorPath);
        return;
    }
    navigate('/error');
};

export const submitSignUp = async ({
    values,
    inviteUuid,
    userExternalId,
    context,
    navigate,
    setSignupSuccess,
    setSubmitting,
}: SubmitSignUpParams) => {
    try {
        await context?.registerWithEmailAndPassword(buildSignupPayload(values, userExternalId), inviteUuid);
        setSignupSuccess(true);

        try {
            // The submit button keeps its spinner while the auto-login retries run
            await retryLogin(
                () =>
                    context?.loginInWithEmailAndPassword({ email: values.email, password: values.password }) ??
                    Promise.resolve(),
            );
            const redirectUrl = await buildPlatformRedirectUrl();
            setSubmitting(false);
            window.location.href = redirectUrl;
        } catch {
            // If auto-login fails after all retries, fallback to login page
            setSubmitting(false);
            setTimeout(() => navigate('/login'), LOGIN_FALLBACK_DELAY_MS);
        }
    } catch (error) {
        handleSignupError(error, navigate);
    }
};

// Signs up through an identity provider and lands the user on its platform
export const runThirdPartySignUp = async (
    signUp: () => Promise<unknown>,
    navigate: NavigateFunction,
    setLoading: (value: boolean) => void,
) => {
    try {
        setLoading(true);
        await signUp();
        const token = localStorage.getItem('token');
        if (token && (isMindy || isOms)) {
            const redirectResponse = await authApi.redirect();
            window.location.href = appendToken(redirectResponse.url, token);
        } else {
            navigate('/login');
        }
    } catch (error) {
        console.error(error);
        navigate('/error');
    } finally {
        setLoading(false);
    }
};
